import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../../components/Spinner";
import {
  confirmPublicInvitation,
  getPublicInvitation,
  reservePublicInvitationSlot,
  type ParentMeetingSlot,
  type PublicParentMeetingInvitation,
} from "../../../services/parentMeetingService";
import { downloadParentMeetingIcs } from "./parentMeetingCalendarExport";

type ParentMeetingInvitationInlineCardProps = {
  token: string | null;
  onCancel: () => void;
  onConfirmed?: () => void;
};

const toDate = (value: any) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0] = value;
    return new Date(y, m - 1, d, h, min);
  }

  if (typeof value === "number") {
    return new Date(value > 1e12 ? value : value * 1000);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateLocale = () => {
  const stored = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : "";
  if (stored?.startsWith("nl")) return "nl-NL";
  if (stored?.startsWith("en")) return "en-US";
  return typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
};

const formatSlot = (start: any, end: any, unknownLabel: string) => {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return unknownLabel;

  const locale = getDateLocale();
  const date = s.toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const startTime = s.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const endTime = e.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${date} · ${startTime} - ${endTime}`;
};

const formatTime = (value: any) => {
  const date = toDate(value);
  if (!date) return "-";

  return date.toLocaleTimeString(getDateLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDayKey = (value: any) => {
  const date = toDate(value);
  if (!date) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDayLabel = (value: any, unknownLabel: string) => {
  const date = toDate(value);
  if (!date) return unknownLabel;

  return date.toLocaleDateString(getDateLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const ParentMeetingInvitationInlineCard: React.FC<ParentMeetingInvitationInlineCardProps> = ({
  token,
  onCancel,
  onConfirmed,
}) => {
  const { t } = useTranslation();
  const [invitation, setInvitation] = useState<PublicParentMeetingInvitation | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reserved, setReserved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDayKeys, setOpenDayKeys] = useState<Set<string>>(new Set());

  const selectedSlot = useMemo<ParentMeetingSlot | undefined>(
    () => invitation?.availableSlots.find((slot) => slot.id === selectedSlotId),
    [invitation, selectedSlotId]
  );

  const groupedSlots = useMemo(() => {
    const groups = new Map<string, { label: string; slots: ParentMeetingSlot[] }>();

    invitation?.availableSlots.forEach((slot) => {
      const start = toDate(slot.startTime);
      if (!start) return;

      const key = getDayKey(slot.startTime);
      if (!key) return;

      const group = groups.get(key) ?? {
        label: formatDayLabel(slot.startTime, t("parentPortal.parent_meetings.unknown_day", { defaultValue: "Unknown day" })),
        slots: [],
      };

      group.slots.push(slot);
      groups.set(key, group);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, group]) => [
        key,
        {
          ...group,
          slots: [...group.slots].sort((a, b) => {
            const startA = toDate(a.startTime)?.getTime() ?? 0;
            const startB = toDate(b.startTime)?.getTime() ?? 0;
            return startA - startB;
          }),
        },
      ] as const);
  }, [invitation, t]);

  useEffect(() => {
    if (!token) {
      setError(t("parentPortal.parent_meetings.invalid_invitation", { defaultValue: "This invitation is invalid." }));
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setConfirmed(false);

      try {
        const data = await getPublicInvitation(token);
        if (cancelled) return;

        setInvitation(data);
        setEmail(data.currentEmail || "");
        setSelectedSlotId(data.selectedSlot?.id || "");
        setReserved(Boolean(data.selectedSlot));

        if (data.selectedSlot?.startTime) {
          const selectedDayKey = getDayKey(data.selectedSlot.startTime);
          setOpenDayKeys(selectedDayKey ? new Set([selectedDayKey]) : new Set());
        } else {
          setOpenDayKeys(new Set());
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(t("parentPortal.parent_meetings.load_failed", { defaultValue: "The invitation could not be loaded or has expired." }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [t, token]);

  const toggleDay = (dayKey: string) => {
    setOpenDayKeys((prev) => {
      const next = new Set(prev);

      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }

      return next;
    });
  };

  async function handleSlotChange(slotId: string) {
    setSelectedSlotId(slotId);
    setReserved(false);

    if (!token || !slotId) return;

    setReserving(true);
    setError(null);

    try {
      await reservePublicInvitationSlot(token, { slotId });
      setReserved(true);
    } catch (err) {
      console.error(err);
      setError(t("parentPortal.parent_meetings.slot_unavailable", { defaultValue: "This time slot is no longer available." }));

      try {
        const data = await getPublicInvitation(token);
        setInvitation(data);
        setSelectedSlotId("");
        setReserved(false);
      } catch (reloadErr) {
        console.error(reloadErr);
      }
    } finally {
      setReserving(false);
    }
  }

  async function handleConfirm(event: React.FormEvent) {
    event.preventDefault();

    if (!token || !selectedSlotId) return;

    setSaving(true);
    setError(null);

    try {
      await confirmPublicInvitation(token, {
        slotId: selectedSlotId,
        email,
      });

      const confirmedSlot =
        selectedSlot ??
        invitation?.availableSlots.find((slot) => slot.id === selectedSlotId);
      
      if (invitation && confirmedSlot) {
        downloadIcsFile(invitation, confirmedSlot);
      }
      
      setConfirmed(true);
      onConfirmed?.();
      setConfirmed(true);
      onConfirmed?.();
    } catch (err) {
      console.error(err);
      setError(t("parentPortal.parent_meetings.confirm_failed", { defaultValue: "The appointment could not be confirmed. This time slot may have just been chosen by someone else." }));
    } finally {
      setSaving(false);
    }
  }

  const downloadIcsFile = (
    invitation: PublicParentMeetingInvitation,
    slot: ParentMeetingSlot,
  ) => {
    const startsAt = toDate(slot.startTime);
    const endsAt = toDate(slot.endTime);
    if (!startsAt || !endsAt) return;

    const title = invitation.title || t("parentPortal.parent_meetings.title", { defaultValue: "Parent meeting" });
    const childName = invitation.childName
      ? t("parentPortal.parent_meetings.calendar_child_suffix", { defaultValue: " for {{childName}}", childName: invitation.childName })
      : "";
    const parentName = invitation.parentName
      ? t("parentPortal.parent_meetings.calendar_parent", { defaultValue: "Parent: {{parentName}}", parentName: invitation.parentName })
      : "";

    const description = [
      invitation.description,
      childName.trim(),
      parentName,
    ]
      .filter(Boolean)
      .join("\n");

    downloadParentMeetingIcs({
      slotId: slot.id,
      title: `${title}${childName}`,
      startsAt,
      endsAt,
      description,
    });
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && confirmed && (
        <div className="rounded-lg bg-green-50 p-5 text-green-800 dark:bg-green-900/30 dark:text-green-300">
          <h4 className="text-lg font-semibold">
            {t("parentPortal.parent_meetings.confirmed_title", { defaultValue: "Appointment confirmed" })}
          </h4>
          <p className="mt-2 text-sm">
            {t("parentPortal.parent_meetings.confirmed_body", {
              defaultValue: "You will receive a confirmation by email. The teacher will receive this confirmation too.",
            })}
          </p>
        </div>
      )}

      {!loading && !confirmed && invitation && (
        <form onSubmit={handleConfirm} className="flex flex-col gap-5">
          <div>
            <h4 className="text-xl font-semibold text-slate-900 dark:text-white">
              {invitation.title}
            </h4>

            {invitation.description && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {invitation.description}
              </p>
            )}

            {(invitation.childName || invitation.parentName) && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                {invitation.childName && (
                  <>
                    {t("parentPortal.parent_meetings.for_child", { defaultValue: "For:" })} <strong>{invitation.childName}</strong>
                  </>
                )}
                {invitation.childName && invitation.parentName && " · "}
                {invitation.parentName && (
                  <>
                    {t("parentPortal.parent_meetings.parent_label", { defaultValue: "Parent:" })} <strong>{invitation.parentName}</strong>
                  </>
                )}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t("parentPortal.parent_meetings.choose_slot", { defaultValue: "Choose a time slot" })}
            </span>

            <div className="space-y-3">
              {groupedSlots.map(([dayKey, group]) => {
                const open = openDayKeys.has(dayKey);
                const selectedInDay = group.slots.some((slot) => slot.id === selectedSlotId);

                return (
                  <div
                    key={dayKey}
                    className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
                  >
                    <button
                      type="button"
                      onClick={() => toggleDay(dayKey)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">
                          {group.label}
                        </div>

                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {t("parentPortal.parent_meetings.slot_count", {
                            defaultValue: "{{count}} time slot(s)",
                            count: group.slots.length,
                          })}
                          {selectedInDay
                            ? t("parentPortal.parent_meetings.selected_suffix", { defaultValue: " · selected" })
                            : ""}
                        </div>
                      </div>

                      <span className={`transition-transform ${open ? "rotate-180" : ""}`}>
                        ▼
                      </span>
                    </button>

                    {open && (
                      <div className="border-t border-slate-100 p-3 dark:border-slate-700">
                        <div className="space-y-2">
                          {group.slots.map((slot) => {
                            const selected = selectedSlotId === slot.id;

                            return (
                              <label
                                key={slot.id}
                                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition ${
                                  selected
                                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200 dark:border-emerald-500 dark:bg-emerald-900/20 dark:ring-emerald-800"
                                    : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/10"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="parentMeetingSlot"
                                  checked={selected}
                                  onChange={() => handleSlotChange(slot.id)}
                                  className="h-4 w-4 text-emerald-600"
                                />

                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                </span>

                                {selected && (
                                  <span className="ml-auto text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                    {t("parentPortal.parent_meetings.selected", { defaultValue: "Selected" })}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedSlot && (
            <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              {reserved
                ? t("parentPortal.parent_meetings.slot_reserved", { defaultValue: "This time slot is temporarily reserved for you." })
                : t("parentPortal.parent_meetings.reserving_slot", { defaultValue: "Reserving time slot..." })}{" "}
              <strong>{formatSlot(selectedSlot.startTime, selectedSlot.endTime, t("parentPortal.parent_meetings.unknown_slot", { defaultValue: "Unknown time slot" }))}</strong>
            </div>
          )}

          <label className="flex flex-col gap-2 text-sm text-slate-700 dark:text-slate-200">
            <span className="font-medium">
              {t("parentPortal.parent_meetings.confirmation_email", { defaultValue: "Email address for confirmation" })}
            </span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-950"
              required
            />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t("parentPortal.parent_meetings.confirmation_email_hint", {
                defaultValue: "You may change this email address. The confirmation will be sent to this address.",
              })}
            </span>
          </label>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              {t("common.actions.close", { defaultValue: "Close" })}
            </button>

            <button
              type="submit"
              disabled={saving || reserving || !reserved || invitation.availableSlots.length === 0}
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {reserving
                ? t("parentPortal.parent_meetings.reserving_slot", { defaultValue: "Reserving time slot..." })
                : t("parentPortal.parent_meetings.confirm", { defaultValue: "Confirm appointment" })}
            </button>
          </div>
        </form>
      )}

      {!loading && (error || confirmed) && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            {t("common.actions.close", { defaultValue: "Close" })}
          </button>
        </div>
      )}
    </div>
  );
};

export default ParentMeetingInvitationInlineCard;
